/**
 * Environment Configuration
 * Read configuration from environment variables.
 *
 * Per-environment URLs (set in .env, env/*.json, or build):
 * - VITE_baseUrl: API base URL.
 * - VITE_patUrl: Associate Process iframe base URL (Plan & Track).
 * - VITE_PLATFORM_BASE_URL: Outermost platform (portal) URL; passed to inner iframe as platformUrl so the innermost
 *   page can know the top window origin (platform → GRI → Plan & Track).
 */

interface EnvConfig {
  /** Current environment */
  env: string;
  /** API base URL */
  apiBaseUrl: string;
  /** Async config (task-owners, task-assignees) base URL. Same as apiBaseUrl. */
  asyncConfigBaseUrl: string;
  /** Plan & Track (PM) embeddable page base URL. Uses VITE_baseUrl (same as API base). */
  planTrackBaseUrl: string;
  /** Associate Process iframe base URL. Uses VITE_patUrl (dev: pat URL; qa/prod can be empty or set). */
  associateUrl: string;
  /**
   * Outermost platform URL (portal). When GRI runs inside platform iframe, pass this to
   * inner iframe so Plan & Track can know the top window origin. Set via VITE_PLATFORM_BASE_URL.
   */
  platformBaseUrl: string;
  /** Application title */
  appTitle: string;
  /** MUI License Key */
  muiLicenseKey: string;
  /** Integrate Manager module name used by e-signature. */
  eSignModuleName: string;
}

/**
 * Get environment configuration.
 * Uses static import.meta.env so Vite can inject env vars at build time.
 * (Jest uses env.mock.ts via moduleNameMapper and never loads this file.)
 */
export const getEnvConfig = (): EnvConfig => {
  const env =
    import.meta.env?.VITE_APP_ENV ?? import.meta.env.MODE ?? "development";
  const apiBaseUrl =
    import.meta.env?.VITE_API_BASE_URL ??
    import.meta.env?.VITE_baseUrl ??
    "http://localhost:8080/api";
  return {
    env,
    apiBaseUrl,
    asyncConfigBaseUrl: apiBaseUrl,
    // Plan & Track associate page base URL: same as API base (VITE_baseUrl).
    planTrackBaseUrl:
      import.meta.env?.VITE_API_BASE_URL ??
      import.meta.env?.VITE_baseUrl ??
      "http://localhost:8080/api",
    // Associate Process iframe base URL (VITE_patUrl).
    associateUrl: import.meta.env?.VITE_patUrl ?? "",
    // Outermost platform URL, passed to Plan & Track iframe so innermost page can use it.
    platformBaseUrl: import.meta.env?.VITE_PLATFORM_BASE_URL ?? "",
    appTitle: import.meta.env?.VITE_APP_TITLE ?? "GRI FE",
    muiLicenseKey: import.meta.env?.VITE_REACT_APP_MUI_LICENSE_KEY ?? "",
    eSignModuleName: import.meta.env?.VITE_ESIGN_MODULE_NAME ?? "regulationsnext",
  };
};

/**
 * Current environment configuration
 */
export const envConfig = getEnvConfig();

/**
 * Get API base URL with service prefix
 * Automatically appends /gri-service if not already present
 */
export const getApiBaseUrl = (): string => {
  const baseUrl = envConfig.apiBaseUrl;
  const servicePrefix = "/gri-service";

  // Check if baseURL already includes the service prefix
  if (baseUrl.includes(servicePrefix)) {
    return baseUrl;
  }

  return `${baseUrl}${servicePrefix}`;
};

/**
 * Host-level API root without `/gri-service`.
 * Use for Plan & Track (`/pm/…`) async URLs on the dynamic form; gri_fe APIs still use `getApiBaseUrl()`.
 */
export const getHostApiBaseUrl = (): string => {
  const baseUrl = envConfig.apiBaseUrl.replace(/\/$/, "");
  const servicePrefix = "/gri-service";
  return baseUrl.includes(servicePrefix)
    ? baseUrl.replace(servicePrefix, "")
    : baseUrl;
};

/**
 * Whether it's development environment
 */
export const isDevelopment = envConfig.env === "development";

/**
 * Whether it's QA environment
 */
export const isQA = envConfig.env === "qa";

/**
 * Whether it's production environment
 */
export const isProduction = envConfig.env === "production";

// Print configuration in development environment
if (isDevelopment) {
  console.log("Environment Config:", envConfig);
}

export default envConfig;
